import React from 'react';
import { Shield, HardHat, Briefcase, Package, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const ProfessionalEquipment = () => {
  const { language } = useLanguage();

  const equipmentCategories = [
    {
      icon: HardHat,
      title: { fr: 'Équipements de Sécurité', en: 'Safety Equipment' },
      description: { fr: 'Protection complète pour tous les secteurs', en: 'Complete protection for all sectors' },
      image: 'https://images.unsplash.com/photo-1662309376159-b95fb193d96b',
      items: [
        { fr: 'Casques de sécurité certifiés', en: 'Certified safety helmets' },
        { fr: 'Gilets haute visibilité', en: 'High visibility vests' },
        { fr: 'Chaussures de sécurité', en: 'Safety shoes' },
        { fr: 'Gants de protection', en: 'Protective gloves' },
        { fr: 'Lunettes et masques', en: 'Goggles and masks' },
        { fr: 'Harnais de sécurité', en: 'Safety harnesses' }
      ]
    },
    {
      icon: Briefcase,
      title: { fr: 'Tenues de Travail Spécialisées', en: 'Specialized Work Clothing' },
      description: { fr: 'Vêtements professionnels sur mesure', en: 'Custom professional clothing' },
      image: 'https://images.unsplash.com/photo-1598679253549-c215e0b9ee1e',
      items: [
        { fr: 'Combinaisons de travail', en: 'Work overalls' },
        { fr: 'Tenues BTP', en: 'Construction clothing' },
        { fr: 'Tenues médicales', en: 'Medical scrubs' },
        { fr: 'Uniformes hôteliers', en: 'Hotel uniforms' },
        { fr: 'Tenues de sécurité', en: 'Security uniforms' },
        { fr: 'Vêtements ignifugés', en: 'Flame-resistant clothing' }
      ]
    },
    {
      icon: Shield,
      title: { fr: 'Équipements Forces de l\'Ordre', en: 'Law Enforcement Equipment' },
      description: { fr: 'Matériel professionnel militaire et police', en: 'Professional military and police equipment' },
      image: 'https://images.unsplash.com/photo-1513828583688-c52646db42da',
      items: [
        { fr: 'Équipements tactiques', en: 'Tactical equipment' },
        { fr: 'Gilets pare-balles', en: 'Bulletproof vests' },
        { fr: 'Matraques et tasers', en: 'Batons and tasers' },
        { fr: 'Lacrymogènes', en: 'Tear gas' },
        { fr: 'Jumelles professionnelles', en: 'Professional binoculars' },
        { fr: 'Sacs d\'intervention', en: 'Tactical bags' }
      ]
    },
    {
      icon: Package,
      title: { fr: 'Accessoires Personnalisés', en: 'Custom Accessories' },
      description: { fr: 'Accessoires marqués à votre image', en: 'Branded accessories' },
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c',
      items: [
        { fr: 'Casquettes brodées', en: 'Embroidered caps' },
        { fr: 'Badges et insignes', en: 'Badges and insignia' },
        { fr: 'Sacs à dos personnalisés', en: 'Custom backpacks' },
        { fr: 'Porte-badges', en: 'Badge holders' },
        { fr: 'Ceintures et accessoires', en: 'Belts and accessories' },
        { fr: 'Gyrophares et bâtons lumineux', en: 'Beacons and light sticks' }
      ]
    }
  ];

  const features = [
    {
      title: { fr: 'Qualité Certifiée', en: 'Certified Quality' },
      description: { fr: 'Tous nos équipements sont conformes aux normes internationales', en: 'All our equipment meets international standards' }
    },
    {
      title: { fr: 'Personnalisation', en: 'Customization' },
      description: { fr: 'Broderie et sérigraphie de votre logo sur tous les articles', en: 'Embroidery and screen printing of your logo on all items' }
    },
    {
      title: { fr: 'Livraison Rapide', en: 'Fast Delivery' },
      description: { fr: 'Stock permanent et livraison sous 48h partout au Sénégal', en: 'Permanent stock and 48h delivery throughout Senegal' }
    },
    {
      title: { fr: 'Conseil Expert', en: 'Expert Advice' },
      description: { fr: 'Notre équipe vous guide dans le choix des équipements adaptés', en: 'Our team guides you in choosing the right equipment' }
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative py-32 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1662309376159-b95fb193d96b)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Équipements Professionnels' : 'Professional Equipment'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr'
              ? 'Votre partenaire de confiance pour tous vos équipements de sécurité et tenues professionnelles'
              : 'Your trusted partner for all your safety equipment and professional clothing'}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              {language === 'fr'
                ? 'PRESTACO est spécialisé dans la fourniture d\'équipements professionnels de haute qualité pour tous les secteurs d\'activité. Que vous soyez dans le BTP, la santé, l\'hôtellerie, la sécurité ou l\'industrie, nous avons les solutions adaptées à vos besoins.'
                : 'PRESTACO specializes in providing high-quality professional equipment for all business sectors. Whether you\'re in construction, healthcare, hospitality, security, or industry, we have the solutions tailored to your needs.'}
            </p>
          </div>
        </div>
      </section>

      {/* Equipment Categories */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Catégories d\'Équipements' : 'Our Equipment Categories'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'fr' ? 'Une gamme complète pour tous vos besoins' : 'A complete range for all your needs'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {equipmentCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  {/* Category Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={category.image}
                      alt={category.title[language]}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  {/* Category Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {category.title[language]}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {category.description[language]}
                    </p>
                    <ul className="space-y-3">
                      {category.items.map((item, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{item[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Pourquoi Choisir PRESTACO ?' : 'Why Choose PRESTACO?'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center bg-gradient-to-br from-blue-50 to-slate-50 border-2 border-gray-200 hover:border-blue-500 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title[language]}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description[language]}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Besoin d\'un Devis Personnalisé ?' : 'Need a Custom Quote?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez notre équipe pour obtenir un devis adapté à vos besoins spécifiques'
              : 'Contact our team to get a quote tailored to your specific needs'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Devis' : 'Request a Quote'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProfessionalEquipment;
